import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { apiClient } from '../../lib/api'
import { useAuthStore } from '../auth/auth.store'
import { NOTIFICATION_ENDPOINTS } from './notifications.endpoints'
import { useNotificationStore } from './notifications.store'
import type { NotificationCheckResponse, NotificationItem } from './notifications.types'

export const getNotificationsService = async (): Promise<NotificationItem[]> => {
  const { data } = await apiClient.get<NotificationItem[]>(NOTIFICATION_ENDPOINTS.LIST)
  return data
}

export const checkNotificationsService = async (): Promise<NotificationCheckResponse> => {
  const { data } = await apiClient.get<NotificationCheckResponse>(NOTIFICATION_ENDPOINTS.CHECK)
  return data
}

export const markNotificationsReadService = async (ids?: (string | number)[]): Promise<void> => {
  await apiClient.post(NOTIFICATION_ENDPOINTS.MARK_READ, ids ? { ids } : {}, {
    _skipSuccessToast: true,
  } as any)
}

export const useNotificationsQuery = (options: { enabled?: boolean } = {}) => {
  const setNotifications = useNotificationStore((state) => state.setNotifications)
  const setUnreadCount = useNotificationStore((state) => state.setUnreadCount)
  const { accessToken } = useAuthStore()

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotificationsService,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY, // Only fetch when explicitly invalidated or on first mount
    enabled: options.enabled !== undefined ? options.enabled && !!accessToken : !!accessToken,
  })

  useEffect(() => {
    if (query.data) {
      setNotifications(query.data)
      // Calculate unread count from the list
      const count = query.data.filter((n) => !n.is_read).length
      setUnreadCount(count)
    }
  }, [query.data, setNotifications, setUnreadCount])

  return query
}

export const useNotificationCheckQuery = (options: { enabled?: boolean } = {}) => {
  const setUnreadCount = useNotificationStore((state) => state.setUnreadCount)
  const prevCountRef = useRef<number | null>(null)
  const queryClient = useQueryClient()
  const { accessToken } = useAuthStore()

  const query = useQuery({
    queryKey: ['notifications', 'check'],
    queryFn: checkNotificationsService,
    refetchInterval: 30000, // 30s polling
    refetchIntervalInBackground: true,
    enabled: options.enabled !== undefined ? options.enabled && !!accessToken : !!accessToken,
  })

  useEffect(() => {
    if (query.data) {
      const newCount = query.data.unread_count

      // If we have a previous count and the new count is higher, refresh the list
      if (prevCountRef.current !== null && newCount > prevCountRef.current) {
        console.log('[NotificationService] New notification detected via polling, invalidating list...')
        // Invalidate the notification list query so it refetches
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      }

      prevCountRef.current = newCount
      setUnreadCount(newCount)
    }
  }, [query.data, setUnreadCount, queryClient])

  return query
}

export const useMarkNotificationsReadMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markNotificationsReadService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'check'] })
    },
  })
}
